import { Injectable, OnDestroy, inject, signal, computed } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ReplaySubject, combineLatest, filter, pairwise, startWith, takeUntil } from 'rxjs';

import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import Social from 'entity/Social';
import SocialRoomSet from 'entity/SocialRoomSet';
import StartingPoint from 'entity/StartingPoint';
import EntryOptionType from 'enums/EntryOptionType';

import { SocialPageService } from '../social-page/services/social-page.service';
import { socialAppSelectors } from '../state/app';

import { RoomPresenceService } from './room/room-presence.service';
import { VideoChatService } from './videochat.service';
import {
  ModalSimpleSocialComponent,
  ModalSimpleSocialData,
} from '../modal-simple-social/modal-simple-social.component';
import { MatDialog } from '@angular/material/dialog';

@Injectable({
  providedIn: 'root',
})
export class ConnectedRoomsService extends Loggable implements OnDestroy {
  private store = inject(Store);
  private dialog = inject(MatDialog);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private roomPresenceService = inject(RoomPresenceService);
  private videoChatService = inject(VideoChatService);
  private socialPageService = inject(SocialPageService);

  private readonly entryOption = signal<EntryOptionType | undefined>(undefined);
  private destroyed$ = new ReplaySubject<boolean>(1);
  public readonly connectedRoomSet = signal<SocialRoomSet | undefined>(undefined);
  public readonly selectedRoomId = signal<string | undefined>(undefined);

  public readonly urlRoomId = signal<string | undefined>(undefined);
  public readonly social = signal<Social | undefined>(undefined);
  private readonly roomId = signal<string | undefined>(undefined);

  private readonly isPresentInRoom = signal<boolean>(false);
  public readonly connectedRoomId = signal<string>('');
  public readonly startingPoint = signal<StartingPoint | undefined>(undefined);

  public constructor() {
    super();
    this.setDisplayName('ConnectedRoomsService');

    this.route.params.pipe(takeUntil(this.destroyed$)).subscribe((params) => {
      // NOTE: params do not update when URL is updated for connected room dropdown selection.
      if (params) {
        const { id, sid } = params;
        if (sid) {
          this.urlRoomId.set(id);
          this.connectedRoomId.set(sid);
        }
      }
    });

    combineLatest([
      this.store.select(socialAppSelectors.getCurrentSocialId),
      this.store.select(socialAppSelectors.getCurrentConnectedRoomSet),
    ])
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe(([roomId, connectedRoomSet]) => {
        this.roomId.set(roomId);
        this.connectedRoomSet.set(connectedRoomSet);
        this.entryOption.set(connectedRoomSet?.entryOption);

        // we should only perform this check if entry configuration is set to starting point
        if (
          connectedRoomSet &&
          connectedRoomSet.socialRoomSetId &&
          connectedRoomSet.entryOption === EntryOptionType.STARTING_POINT
        ) {
          this.socialPageService
            .getStartingPoint(connectedRoomSet.socialRoomSetId)
            .then((startingPoint) => {
              if (!startingPoint) {
                return;
              }
              this.startingPoint.set(startingPoint);
              if (startingPoint.socialId !== this.roomId() && this.roomId()) {
                // navigate to the correct social, this should be handled in the guard but duplicating logic here for safety
                this.router
                  .navigate(['/connected-room', connectedRoomSet.socialRoomSetId, startingPoint.socialId])
                  .catch((err) => {
                    this.error(err);
                  });
              }
            })
            .catch((err) => {
              this.error(err);
            });
        }
      });

    this.store
      .select(socialAppSelectors.isJoined)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentInRoom) => this.isPresentInRoom.set(isPresentInRoom));

    /**
     * The pairwise startWith() hack is an unpleasant workaround for this.router.params
     * not updating when we add the ID to the URL in connected room cases. It ensures we can
     * distinguish between cases where the social is updated because the social as specified in the URL,
     * or cases where the social is updated because it was the first
     * social in a connected room where no roomID was specified in the URL.
     */
    this.store
      .select(socialAppSelectors.getCurrentSocial)
      .pipe(filter(notNil), startWith({} as Social), pairwise(), takeUntil(this.destroyed$))
      .subscribe(([prevSocial, social]) => {
        if (this.connectedRoomId()) {
          this.selectedRoomId.set(social.socialId);
          if (!this.urlRoomId() && !!prevSocial.socialId) {
            // in this case, user has selected a room and URL has been updated
            this.urlRoomId.set(social.socialId);
          }
        }
        this.social.set(social);

        // Init social and fetch room settings
        if (social?.socialId) {
          this.videoChatService.initCurrentSocial(social.socialId);
        }
      });
  }

  // currently only being called from welcome-devices-form
  // TODO: refactor and clean up somehow
  public onRoomChange({ socialId }: Social): void {
    this.logUserAction('onRoomChange', 'select', { selectedRoomId: socialId, previousRoomId: this.roomId() });
    if (!socialId || socialId === this.roomId()) {
      return;
    }

    if (!this.isPresentInRoom()) {
      this.roomPresenceService.changeInitialRoom(socialId);
    }

    const data = {
      modalTitle: 'Change Room',
      text: 'Are you sure you want to leave this room?',
      primaryButtonText: 'Continue',
      tertiaryButtonText: 'Cancel',
    };

    const dialogRef = this.dialog.open<ModalSimpleSocialComponent, ModalSimpleSocialData>(ModalSimpleSocialComponent, {
      data,
    });

    dialogRef.afterClosed().subscribe((confirm: 'primary' | 'tertiary' | undefined) => {
      if (confirm === 'primary') {
        this.roomPresenceService.changeRoom(socialId);
        return;
      }
      this.selectedRoomId.set(socialId);
    });
  }

  public readonly dropdownDisabled = computed(() => {
    if (this.isPresentInRoom()) {
      return false;
    }
    const entryOptionValue = this.entryOption();
    return entryOptionValue === EntryOptionType.CUSTOM_RULES || entryOptionValue === EntryOptionType.STARTING_POINT;
  });

  public readonly isConnected = computed(() => {
    const connectedRoomSetValue = this.connectedRoomSet();
    return (connectedRoomSetValue?.socials ?? []).length > 1;
  });

  public readonly selectedConnectedRoom = computed(() => {
    const selectedRoomIdValue = this.selectedRoomId();
    const connectedRoomSetValue = this.connectedRoomSet();
    if (!selectedRoomIdValue || !connectedRoomSetValue) {
      return undefined;
    }
    return connectedRoomSetValue.socials?.find((c) => c.socialId === selectedRoomIdValue) ?? undefined;
  });

  public readonly noConnectedRoomSelected = computed(() => {
    return !!this.connectedRoomId() && !this.urlRoomId();
  });

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
